import {
    BADGES,
    BUCKETS,
    INTEGRATION_BADGE_LABELS,
    ONBOARDING_RELATIONSHIPS,
    RELATIONSHIP_SUBTITLES,
    SHIPPER_BADGES,
} from './constants';

/** The account a row describes: `carrier` on organization_carrier_statuses, `shipper` on the shipper view. */
const accountOf = (row) => row.carrier || row.shipper;

const SEARCHABLE = (row) => {
    const account = accountOf(row);
    return [account?.business_name, account?.email, account?.user?.email, row.carrier_id || row.shipper_id];
};

const byName = (a, b) => (accountOf(a)?.business_name || '').localeCompare(accountOf(b)?.business_name || '');

/** Rows whose name, email or account ID contains `search`, case-insensitively. Every row when `search` is empty. */
export const filterRows = (rows, search) => {
    const query = (search || '').trim().toLowerCase();
    if (!query) return rows || [];
    return (rows || []).filter((row) => SEARCHABLE(row).some((value) => value?.toLowerCase().includes(query)));
};

/** Whether the carrier onboarding view lists a row: Fulfillment Partners and Motor Carriers, not Flex Centers. */
export const isOnboardingCarrier = (row) => ONBOARDING_RELATIONSHIPS.includes(row.relationship);

/** No filters picked: every row shows. */
export const NO_FILTERS = { types: [], badges: [] };

/**
 * Rows matching the onboarding filter. `types` are relationships (PARTNER, CONTRACTOR) and `badges` are badge
 * keys. Within a group a row needs any one of the picks; across groups it needs both. An empty group doesn't filter.
 */
export const filterByFacets = (rows, { types = [], badges = [] } = NO_FILTERS, badgeList = BADGES) =>
    rows.filter((row) => {
        if (types.length && !types.includes(row.relationship)) return false;
        if (badges.length && !badgeList.some((badge) => badges.includes(badge.key) && row[badge.flag])) return false;
        return true;
    });

/** The rows in one tab, sorted by name. */
export const rowsInBucket = (rows, bucket) => {
    const inBucket = rows.filter((row) => row[bucket.flag]);
    return inBucket.sort(byName);
};

/**
 * The section of `bucket` a row shows in: the last of the tab's `subBuckets` whose flag it meets, as the view's flags
 * overlap (a signed-up carrier that has finished every step meets In Progress and Awaiting Compliance). The first
 * section when it meets none.
 */
export const subBucketFor = (row, bucket) =>
    [...bucket.subBuckets].reverse().find((subBucket) => row[subBucket.flag]) || bucket.subBuckets[0];

/**
 * A tab's rows split into its sections, in display order, leaving out empty ones. Rows keep their order within a
 * section.
 *
 * @returns {{ key: string, label: string, rows: object[] }[]}
 */
export const groupBySubBucket = (rows, bucket) => {
    const rowsByKey = rows.reduce((byKey, row) => {
        const { key } = subBucketFor(row, bucket);
        (byKey[key] ??= []).push(row);

        return byKey;
    }, {});

    return bucket.subBuckets
        .filter((subBucket) => rowsByKey[subBucket.key])
        .map((subBucket) => ({ key: subBucket.key, label: subBucket.label, rows: rowsByKey[subBucket.key] }));
};

/** `{ [bucket key]: row count }` for every tab. */
export const bucketCounts = (rows, buckets = BUCKETS) =>
    Object.fromEntries(
        buckets.map((bucket) => [bucket.key, rows.reduce((count, row) => (row[bucket.flag] ? count + 1 : count), 0)])
    );

/** The `?bucket=` value for a tab key: the key in lower case. */
export const bucketParam = (key) => key.toLowerCase();

/** The tab a `?bucket=` value names, or the first tab for a junk or missing value or another kind's tab. */
export const bucketForParam = (buckets, param) => buckets.find((b) => bucketParam(b.key) === param) || buckets[0];

/** The card subtitle for a row's relationship to the org. */
export const subtitleFor = (row) => RELATIONSHIP_SUBTITLES[row.relationship] || '';

/**
 * The badges a card shows, in order. The integration badge names the integration's status.
 *
 * @returns {{ key: string, label: string, variant: string }[]}
 */
export const badgesFor = (row) =>
    BADGES.reduce((shown, badge) => {
        if (!row[badge.flag]) return shown;
        const label =
            badge.key === 'INTEGRATION' ? INTEGRATION_BADGE_LABELS[row.integration_status] || badge.label : badge.label;
        shown.push({ key: badge.key, label, variant: badge.variant });
        return shown;
    }, []);

/** The address an invite goes to: the client's email, else its user's. */
const inviteEmail = (row) => row.carrier?.email || row.carrier?.user?.email || null;

/** A Fulfillment Partner or Contractor that hasn't signed up can be sent a fresh invite. */
const canReinvite = (row) => ['PARTNER', 'CONTRACTOR'].includes(row.relationship) && !row.logged_in;

/** Flex Centers and Fulfillment Partners carry the "No Integrations Required" flag. */
const hasIntegrationsFlag = (row) => ['OWNED', 'PARTNER'].includes(row.relationship);

/**
 * What the options panel offers for the selected rows:
 * - `invitable`: Partners and Contractors that haven't signed up and have an email
 * - `markNoIntegrations`: Flex Centers and Partners still requiring integrations, on the Integrations tab only
 * - `requireIntegrations`: Flex Centers and Partners marked No Integrations Required, on any tab
 */
export const selectionActions = (selectedRows, bucketKey) => ({
    invitable: selectedRows.filter((row) => canReinvite(row) && inviteEmail(row)),
    markNoIntegrations:
        bucketKey === 'INTEGRATIONS'
            ? selectedRows.filter((row) => hasIntegrationsFlag(row) && row.integrations_required)
            : [],
    requireIntegrations: selectedRows.filter((row) => hasIntegrationsFlag(row) && !row.integrations_required),
});

/** A shipper card's badges, in order, shaped like badgesFor's. */
export const shipperBadgesFor = (row) =>
    SHIPPER_BADGES.reduce((shown, badge) => {
        if (row[badge.flag]) shown.push({ key: badge.key, label: badge.label, variant: badge.variant });
        return shown;
    }, []);

/**
 * What a card shows for its row: carrier rows (the default) or shipper rows.
 *
 * @returns {{ id: string, name: string, subtitle: string, badges: object[] }}
 */
export const describeCarrierRow = (row) => ({
    id: row.carrier_id,
    name: row.carrier?.business_name || 'Unnamed carrier',
    subtitle: subtitleFor(row),
    badges: badgesFor(row),
});

/** describeCarrierRow for a shipper row: its email as the subtitle. */
export const describeShipperRow = (row) => ({
    id: row.shipper_id,
    name: row.shipper?.business_name || 'Unnamed shipper',
    subtitle: row.shipper?.email || row.shipper?.user?.email || '',
    badges: shipperBadgesFor(row),
});

/**
 * What the shipper options panel offers for the selected rows:
 * - `markNoIntegrations`: shippers still requiring integrations, on the Integrations tab only
 * - `requireIntegrations`: shippers marked No Integrations Required, on any tab
 */
export const shipperSelectionActions = (selectedRows, bucketKey) => ({
    markNoIntegrations: bucketKey === 'INTEGRATIONS' ? selectedRows.filter((row) => row.integrations_required) : [],
    requireIntegrations: selectedRows.filter((row) => !row.integrations_required),
});
